import React, { useMemo } from "react";
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Position,
  Handle,
  MarkerType,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

const NODE_WIDTH = 300;
const NODE_HEIGHT = 110;
const H_GAP = 56;
const V_GAP = 120;

const STATUS = {
  USED_IN_TOTAL: "USED_IN_TOTAL",
  EXCLUDED_FROM_TOTAL_USED_IN_BREAKDOWN:
    "EXCLUDED_FROM_TOTAL_USED_IN_BREAKDOWN",
  EXCLUDED_FROM_TOTAL: "EXCLUDED_FROM_TOTAL",
  UNUSED: "UNUSED",
};

const getDeviceStatus = (deviceId, usedIds, excludedIds, breakdownIds) => {
  if (usedIds.has(deviceId)) {
    return STATUS.USED_IN_TOTAL;
  }

  if (excludedIds.has(deviceId) && breakdownIds.has(deviceId)) {
    return STATUS.EXCLUDED_FROM_TOTAL_USED_IN_BREAKDOWN;
  }

  if (excludedIds.has(deviceId)) {
    return STATUS.EXCLUDED_FROM_TOTAL;
  }

  if (breakdownIds.has(deviceId)) {
    return STATUS.EXCLUDED_FROM_TOTAL_USED_IN_BREAKDOWN;
  }

  return STATUS.UNUSED;
};

const getStatusMeta = (status) => {
  switch (status) {
    case STATUS.USED_IN_TOTAL:
      return {
        badge: "Usado en total",
        sublabel: null,
        badgeBg: "#15803d",
        border: "#22c55e",
        bg: "#f0fdf4",
        title: "#14532d",
        text: "#166534",
      };

    case STATUS.EXCLUDED_FROM_TOTAL_USED_IN_BREAKDOWN:
      return {
        badge: "Excluido del total",
        sublabel: "Usado en desglose",
        badgeBg: "#1d4ed8",
        border: "#60a5fa",
        bg: "#eff6ff",
        title: "#1e3a8a",
        text: "#1d4ed8",
      };

    case STATUS.EXCLUDED_FROM_TOTAL:
      return {
        badge: "Excluido del total",
        sublabel: null,
        badgeBg: "#b45309",
        border: "#f59e0b",
        bg: "#fffbeb",
        title: "#92400e",
        text: "#b45309",
      };

    case STATUS.UNUSED:
    default:
      return {
        badge: "Sin uso",
        sublabel: null,
        badgeBg: "#6b7280",
        border: "#d1d5db",
        bg: "#f9fafb",
        title: "#111827",
        text: "#6b7280",
      };
  }
};

const DeviceNode = ({ data }) => {
  const { name, mac, meta } = data;

  return (
    <div
      style={{
        width: NODE_WIDTH,
        minHeight: NODE_HEIGHT,
        background: meta.bg,
        border: `1.5px solid ${meta.border}`,
        borderRadius: 16,
        boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
        padding: "14px 16px",
        boxSizing: "border-box",
      }}
    >
      <Handle
        type="target"
        position={Position.Top}
        style={{ opacity: 0, pointerEvents: "none" }}
      />

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 12,
          marginBottom: 10,
        }}
      >
        <div
          style={{
            flex: 1,
            fontSize: 15,
            fontWeight: 800,
            color: meta.title,
            lineHeight: 1.25,
            wordBreak: "break-word",
          }}
        >
          {name || "Sin nombre"}
        </div>

        <div
          style={{
            flexShrink: 0,
            background: meta.badgeBg,
            color: "#fff",
            borderRadius: 999,
            padding: "4px 10px",
            fontSize: 11,
            fontWeight: 700,
            whiteSpace: "nowrap",
          }}
        >
          {meta.badge}
        </div>
      </div>

      <div
        style={{
          fontSize: 13,
          color: "#374151",
          fontWeight: 600,
          letterSpacing: 0.2,
          wordBreak: "break-word",
          marginBottom: meta.sublabel ? 8 : 0,
        }}
      >
        {mac || "Sin MAC"}
      </div>

      {meta.sublabel ? (
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            borderRadius: 999,
            padding: "4px 10px",
            fontSize: 11,
            fontWeight: 700,
            background: "rgba(255,255,255,0.65)",
            color: meta.text,
            border: `1px solid ${meta.border}`,
          }}
        >
          {meta.sublabel}
        </div>
      ) : null}

      <Handle
        type="source"
        position={Position.Bottom}
        style={{ opacity: 0, pointerEvents: "none" }}
      />
    </div>
  );
};

const nodeTypes = {
  deviceNode: DeviceNode,
};

const buildGraphModel = (deviceResolution) => {
  const nodes = [];
  const edges = [];

  if (!deviceResolution) {
    return {
      nodes,
      edges,
      isolatedCards: [],
      hasHierarchy: false,
    };
  }

  const {
    devices_considered = [],
    devices_used = [],
    devices_excluded = [],
    breakdown_devices = [],
    relations_applied = [],
  } = deviceResolution;

  const usedIds = new Set(devices_used.map((d) => Number(d.id)));
  const excludedIds = new Set(devices_excluded.map((d) => Number(d.id)));
  const breakdownIds = new Set(breakdown_devices.map((d) => Number(d.id)));

  const devicesMap = new Map(
    devices_considered.map((device) => [Number(device.id), device])
  );

  const childrenMap = new Map();
  const parentCount = new Map();

  devices_considered.forEach((device) => {
    const id = Number(device.id);
    childrenMap.set(id, []);
    parentCount.set(id, 0);
  });

  relations_applied.forEach((relation) => {
    const parentId = Number(relation.parent_device_id);
    const childId = Number(relation.child_device_id);

    if (!devicesMap.has(parentId) || !devicesMap.has(childId)) return;

    childrenMap.get(parentId).push(childId);
    parentCount.set(childId, (parentCount.get(childId) || 0) + 1);

    edges.push({
      id: `e-${parentId}-${childId}`,
      source: String(parentId),
      target: String(childId),
      type: "smoothstep",
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 18,
        height: 18,
        color: "#94a3b8",
      },
      style: {
        stroke: "#94a3b8",
        strokeWidth: 2.2,
      },
      selectable: false,
      focusable: false,
    });
  });

  const rootIds = [];
  const isolatedIds = [];

  devices_considered.forEach((device) => {
    const id = Number(device.id);
    const hasParent = (parentCount.get(id) || 0) > 0;
    const hasChildren = (childrenMap.get(id) || []).length > 0;

    if (!hasParent) {
      rootIds.push(id);
    }

    if (!hasParent && !hasChildren) {
      isolatedIds.push(id);
    }
  });

  const hierarchyRoots = rootIds.filter((id) => !isolatedIds.includes(id));

  const levelMap = new Map();
  const queue = hierarchyRoots.map((id) => ({ id, level: 0 }));
  const visited = new Set();

  while (queue.length) {
    const { id, level } = queue.shift();
    const visitKey = `${id}-${level}`;

    if (visited.has(visitKey)) continue;
    visited.add(visitKey);

    const prevLevel = levelMap.get(id);
    if (prevLevel == null || level < prevLevel) {
      levelMap.set(id, level);
    }

    const children = childrenMap.get(id) || [];
    children.forEach((childId) => {
      queue.push({ id: childId, level: level + 1 });
    });
  }

  const levels = new Map();

  devices_considered.forEach((device) => {
    const id = Number(device.id);
    if (isolatedIds.includes(id)) return;

    const level = levelMap.get(id);
    if (level == null) return;

    if (!levels.has(level)) levels.set(level, []);
    if (!levels.get(level).includes(id)) {
      levels.get(level).push(id);
    }
  });

  const sortedLevels = [...levels.entries()].sort((a, b) => a[0] - b[0]);

  sortedLevels.forEach(([level, ids]) => {
    const totalWidth = ids.length * NODE_WIDTH + (ids.length - 1) * H_GAP;
    const startX = -totalWidth / 2;

    ids.forEach((id, index) => {
      const device = devicesMap.get(id);
      const status = getDeviceStatus(id, usedIds, excludedIds, breakdownIds);
      const meta = getStatusMeta(status);

      nodes.push({
        id: String(id),
        type: "deviceNode",
        position: {
          x: startX + index * (NODE_WIDTH + H_GAP),
          y: level * (NODE_HEIGHT + V_GAP),
        },
        draggable: false,
        selectable: false,
        connectable: false,
        focusable: false,
        data: {
          id,
          name: device?.name,
          mac: device?.mac,
          meta,
        },
      });
    });
  });

  const isolatedCards = isolatedIds.map((id) => {
    const device = devicesMap.get(id);
    const status = getDeviceStatus(id, usedIds, excludedIds, breakdownIds);
    const meta = getStatusMeta(status);

    return {
      id: String(id),
      name: device?.name,
      mac: device?.mac,
      meta,
    };
  });

  return {
    nodes,
    edges,
    isolatedCards,
    hasHierarchy: nodes.length > 0,
  };
};

const Legend = () => {
  const itemStyle = (bg) => ({
    display: "inline-flex",
    alignItems: "center",
    background: bg,
    color: "#fff",
    borderRadius: 999,
    padding: "6px 12px",
    fontSize: 12,
    fontWeight: 700,
  });

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 10,
        marginBottom: 18,
      }}
    >
      <div style={itemStyle("#15803d")}>Usado en total</div>
      <div style={itemStyle("#1d4ed8")}>Excluido del total + usado en desglose</div>
      <div style={itemStyle("#b45309")}>Excluido del total</div>
      <div style={itemStyle("#6b7280")}>Sin uso</div>
    </div>
  );
};

const IsolatedCard = ({ item }) => {
  return (
    <div
      style={{
        background: item.meta.bg,
        border: `1.5px solid ${item.meta.border}`,
        borderRadius: 14,
        padding: "14px 16px",
        boxShadow: "0 8px 20px rgba(15, 23, 42, 0.05)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "flex-start",
          marginBottom: 10,
        }}
      >
        <div
          style={{
            fontSize: 14,
            fontWeight: 800,
            color: item.meta.title,
            lineHeight: 1.25,
            wordBreak: "break-word",
          }}
        >
          {item.name || "Sin nombre"}
        </div>

        <div
          style={{
            background: item.meta.badgeBg,
            color: "#fff",
            borderRadius: 999,
            padding: "4px 10px",
            fontSize: 11,
            fontWeight: 700,
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          {item.meta.badge}
        </div>
      </div>

      <div
        style={{
          fontSize: 13,
          color: "#374151",
          fontWeight: 600,
          wordBreak: "break-word",
          marginBottom: item.meta.sublabel ? 8 : 0,
        }}
      >
        {item.mac || "Sin MAC"}
      </div>

      {item.meta.sublabel ? (
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            borderRadius: 999,
            padding: "4px 10px",
            fontSize: 11,
            fontWeight: 700,
            background: "rgba(255,255,255,0.65)",
            color: item.meta.text,
            border: `1px solid ${item.meta.border}`,
          }}
        >
          {item.meta.sublabel}
        </div>
      ) : null}
    </div>
  );
};

const SectionTitle = ({ title, subtitle }) => (
  <div style={{ marginBottom: 12 }}>
    <div
      style={{
        fontSize: 16,
        fontWeight: 800,
        color: "#111827",
        marginBottom: 4,
      }}
    >
      {title}
    </div>
    {subtitle ? (
      <div
        style={{
          fontSize: 13,
          color: "#6b7280",
        }}
      >
        {subtitle}
      </div>
    ) : null}
  </div>
);

const EmptyState = ({ text }) => (
  <div
    style={{
      padding: 18,
      border: "1px solid #e5e7eb",
      borderRadius: 16,
      background: "#fff",
      color: "#6b7280",
      fontSize: 14,
    }}
  >
    {text}
  </div>
);

const DeviceGraphInner = ({ data }) => {
  const { nodes, edges, isolatedCards, hasHierarchy } = useMemo(
    () => buildGraphModel(data),
    [data]
  );

  if (!data) {
    return <EmptyState text="No hay datos de device_resolution para mostrar." />;
  }

  if (!nodes.length && !isolatedCards.length) {
    return <EmptyState text="No hay dispositivos para mostrar en el diagrama." />;
  }

  return (
    <div style={{ width: "100%" }}>
      <div style={{ marginBottom: 14 }}>
        <div
          style={{
            fontSize: 22,
            fontWeight: 800,
            color: "#111827",
            marginBottom: 6,
          }}
        >
          Jerarquía de dispositivos
        </div>

        <div
          style={{
            fontSize: 14,
            color: "#6b7280",
            maxWidth: 920,
            lineHeight: 1.5,
          }}
        >
          Vista fija de relaciones entre dispositivos. Cada nodo indica su rol
          dentro del cálculo energético: si participa en el total, si se excluye
          del total para evitar duplicación y si además se utiliza en el desglose.
        </div>
      </div>

      <Legend />

      {hasHierarchy && (
        <div style={{ marginBottom: 28 }}>
          <SectionTitle
            title="Jerarquía principal"
            subtitle="Relaciones padre-hijo aplicadas sobre los dispositivos considerados."
          />

          <div
            style={{
              width: "100%",
              height: 480,
              border: "1px solid #e5e7eb",
              borderRadius: 20,
              overflow: "hidden",
              background: "#ffffff",
            }}
          >
            <ReactFlow
              nodes={nodes}
              edges={edges}
              nodeTypes={nodeTypes}
              fitView
              fitViewOptions={{ padding: 0.16 }}
              proOptions={{ hideAttribution: true }}
              nodesDraggable={false}
              nodesConnectable={false}
              elementsSelectable={false}
              panOnDrag={false}
              panOnScroll={false}
              zoomOnScroll={false}
              zoomOnPinch={false}
              zoomOnDoubleClick={false}
              selectionOnDrag={false}
              preventScrolling={false}
              minZoom={1}
              maxZoom={1}
              defaultViewport={{ x: 0, y: 0, zoom: 1 }}
              deleteKeyCode={null}
              multiSelectionKeyCode={null}
            >
              <Background
                variant="dots"
                gap={24}
                size={1}
                color="#f1f5f9"
              />
            </ReactFlow>
          </div>
        </div>
      )}

      {!!isolatedCards.length && (
        <div>
          <SectionTitle
            title="Dispositivos sin relación jerárquica"
            subtitle="Equipos considerados que no tienen vínculo padre-hijo aplicado en esta vista."
          />

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: 16,
            }}
          >
            {isolatedCards.map((item) => (
              <IsolatedCard key={item.id} item={item} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const DeviceGraph = ({ data }) => {
  return (
    <ReactFlowProvider>
      <DeviceGraphInner data={data} />
    </ReactFlowProvider>
  );
};

export default DeviceGraph;